/**
 * Deal Pipeline Page.
 *
 * Organized into three distinct visual sections:
 * 1. KPI summary bar — key metrics at a glance
 * 2. Kanban board — stage columns with deal cards
 * 3. Revenue forecast — 3-month outlook
 *
 * Each section has clear visual separation with section headers,
 * dividers, and contained layouts.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Trash2,
  AlertTriangle,
  Clock,
  TrendingUp,
  Target,
  DollarSign,
  BarChart3,
  Calendar,
  ChevronRight,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import { usePipelineSummary, useForecast } from "@/features/crm/api";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import type { PaginatedResponse } from "@/types/common";

interface Deal {
  id: string;
  title: string;
  customer_name: string | null;
  customer_id: string | null;
  stage: string;
  pipeline: string;
  estimated_value: number;
  weighted_value: number;
  win_probability: number;
  quoted_price: number | null;
  next_follow_up: string | null;
  source: string | null;
  lead_score: number;
  forecast_category: string | null;
  stage_entered_at: string | null;
  last_activity_at: string | null;
  trade_in_value: number | null;
  created_at: string;
  updated_at: string;
}

const STAGES = [
  {
    key: "lead",
    label: "Lead",
    prob: 10,
    color: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  },
  {
    key: "qualified",
    label: "Qualified",
    prob: 20,
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
  },
  {
    key: "quoted",
    label: "Quoted",
    prob: 30,
    color:
      "bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300",
  },
  {
    key: "negotiating",
    label: "Negotiating",
    prob: 60,
    color:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
  },
  {
    key: "deposit_received",
    label: "Deposit",
    prob: 80,
    color:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
  },
] as const;

const SOURCES = [
  "website",
  "referral",
  "trade_show",
  "cold_call",
  "auction",
  "walk_in",
  "phone",
  "adf_lead",
  "repeat_customer",
];

const STALE_THRESHOLD_DAYS = 7;
const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    v,
  );
const fmtCompact = (v: number) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(v);

function isStale(stageEnteredAt: string | null): boolean {
  if (!stageEnteredAt) return false;
  return (
    (Date.now() - new Date(stageEnteredAt).getTime()) / 86400000 >
    STALE_THRESHOLD_DAYS
  );
}

function DealsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<PaginatedResponse<Deal>>({
    queryKey: ["deals"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/deals?page_size=200"))
        .data as PaginatedResponse<Deal>,
  });
  const { data: pipelineMetrics } = usePipelineSummary("sales");
  const { data: forecast } = useForecast("sales");

  const createMutation = useMutation({
    mutationFn: async (d: Record<string, unknown>) =>
      (await apiClient.post("/api/v1/deals", d)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["pipeline-summary"] });
      toast.success("Deal created");
    },
    onError: () => toast.error("Failed to create deal"),
  });
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...d }: Record<string, unknown>) =>
      (await apiClient.put(`/api/v1/deals/${id}`, d)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["pipeline-summary"] });
      toast.success("Deal updated");
    },
    onError: () => toast.error("Failed to update deal"),
  });
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/deals/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["pipeline-summary"] });
      toast.success("Deal deleted");
    },
    onError: () => toast.error("Failed to delete deal"),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [value, setValue] = useState("");
  const [source, setSource] = useState("");

  const deals = data?.items || [];

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Deal Pipeline
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Track prospects from lead to close.
          </p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? (
            "Cancel"
          ) : (
            <>
              <Plus className="h-4 w-4" /> New Deal
            </>
          )}
        </Button>
      </div>

      {/* ── Create form ── */}
      {showCreate && (
        <Card>
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">
            New Deal
          </h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate({
                title,
                customer_name: customerName || undefined,
                estimated_value: parseFloat(value) || 0,
                source: source || undefined,
              });
              setShowCreate(false);
              setTitle("");
              setCustomerName("");
              setValue("");
              setSource("");
            }}
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          >
            <Input
              label="Deal Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder="Smith Farm — 2 tractors"
            />
            <Input
              label="Customer Name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="John Smith"
            />
            <Input
              label="Estimated Value"
              type="number"
              step="0.01"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="65000"
            />
            <div>
              <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                Source
              </label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
              >
                <option value="">Select source...</option>
                {SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {s.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button
                type="submit"
                size="sm"
                isLoading={createMutation.isPending}
              >
                Create Deal
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* ══════════════════════════════════════════════
          §1  KPI Summary
          ══════════════════════════════════════════════ */}
      {pipelineMetrics && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="h-4 w-4 text-[var(--text-tertiary)]" />
            <h3 className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
              Pipeline Overview
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[
              {
                icon: Target,
                label: "Deals",
                value: String(pipelineMetrics.total_deals),
                color: "text-brand-600",
              },
              {
                icon: DollarSign,
                label: "Pipeline",
                value: fmtCompact(pipelineMetrics.total_value),
                color: "text-emerald-600",
              },
              {
                icon: TrendingUp,
                label: "Weighted",
                value: fmtCompact(pipelineMetrics.total_weighted),
                color: "text-violet-600",
              },
              {
                icon: BarChart3,
                label: "Win Rate",
                value: `${pipelineMetrics.win_rate}%`,
                color: "text-blue-600",
              },
              {
                icon: Clock,
                label: "Avg Cycle",
                value: `${pipelineMetrics.avg_deal_cycle_days}d`,
                color: "text-amber-600",
              },
            ].map((kpi) => (
              <Card key={kpi.label} padding="sm" className="metric-card">
                <div className="flex items-center gap-2">
                  <kpi.icon className={`h-4 w-4 ${kpi.color}`} />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                    {kpi.label}
                  </span>
                </div>
                <p className={`text-xl font-bold mt-1.5 ${kpi.color}`}>
                  {kpi.value}
                </p>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════
          §2  Kanban Board
          ══════════════════════════════════════════════ */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Target className="h-4 w-4 text-[var(--text-tertiary)]" />
          <h3 className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
            Active Deals by Stage
          </h3>
        </div>

        {isLoading ? (
          <LoadingSpinner className="py-12" />
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 xl:grid-cols-5">
            {STAGES.map((stage) => {
              const stageDeals = deals.filter((d) => d.stage === stage.key);
              const totalValue = stageDeals.reduce(
                (s, d) => s + d.estimated_value,
                0,
              );
              const totalWeighted = stageDeals.reduce(
                (s, d) => s + d.weighted_value,
                0,
              );
              const staleCount = stageDeals.filter((d) =>
                isStale(d.stage_entered_at),
              ).length;

              return (
                <div
                  key={stage.key}
                  className="rounded-[var(--radius-lg)] border-[0.5px] border-[var(--border-subtle)] bg-[var(--surface-sunken)]/50 overflow-hidden"
                >
                  {/* Column header */}
                  <div className="px-3 py-2.5 border-b border-[var(--border-subtle)] bg-[var(--surface-base)]/60">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center rounded-[var(--radius-pill)] px-2 py-0.5 text-[11px] font-semibold ${stage.color}`}
                        >
                          {stage.label}
                        </span>
                        <span className="text-xs font-semibold text-[var(--text-primary)]">
                          {stageDeals.length}
                        </span>
                      </div>
                      <span className="text-[10px] text-[var(--text-tertiary)]">
                        {stage.prob}%
                      </span>
                    </div>
                    <div className="flex justify-between mt-1 text-[10px]">
                      <span className="text-[var(--text-tertiary)] tabular-nums">
                        {fmt(totalValue)}
                      </span>
                      <span className="text-violet-600 tabular-nums font-medium">
                        {fmt(totalWeighted)} wtd
                      </span>
                    </div>
                    {staleCount > 0 && (
                      <div className="flex items-center gap-1 mt-1 text-[10px] text-amber-600">
                        <AlertTriangle className="h-3 w-3" />
                        {staleCount} stale deal{staleCount !== 1 ? "s" : ""}
                      </div>
                    )}
                  </div>

                  {/* Deal cards */}
                  <div className="p-2 space-y-2 min-h-[80px]">
                    {stageDeals.map((deal) => {
                      const stale = isStale(deal.stage_entered_at);
                      return (
                        <div
                          key={deal.id}
                          className={`rounded-[var(--radius-md)] bg-[var(--surface-base)] border-[0.5px] p-3 transition-all hover:shadow-[var(--shadow-sm)] ${
                            stale
                              ? "border-amber-400/50 dark:border-amber-600/30"
                              : "border-[var(--border-subtle)]"
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <h4 className="text-sm font-semibold text-[var(--text-primary)] leading-tight">
                              {deal.title}
                            </h4>
                            <button
                              onClick={() => deleteMutation.mutate(deal.id)}
                              className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)] ml-2 shrink-0 transition-colors"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>

                          {deal.customer_name &&
                            (deal.customer_id ? (
                              <Link
                                to={`/customers/${deal.customer_id}`}
                                className="text-xs text-brand-600 hover:underline"
                              >
                                {deal.customer_name}
                              </Link>
                            ) : (
                              <p className="text-xs text-[var(--text-secondary)]">
                                {deal.customer_name}
                              </p>
                            ))}

                          <div className="mt-2 flex items-baseline justify-between">
                            <span className="text-sm font-bold tabular-nums text-brand-600">
                              {fmt(deal.estimated_value)}
                            </span>
                            <span className="text-[10px] font-medium text-violet-600 tabular-nums">
                              {deal.win_probability}%
                            </span>
                          </div>

                          {/* Tags */}
                          {(deal.source ||
                            stale ||
                            deal.forecast_category ||
                            (deal.trade_in_value != null &&
                              deal.trade_in_value > 0)) && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {deal.source && (
                                <span className="micro-badge micro-badge-blue">
                                  {deal.source.replace("_", " ")}
                                </span>
                              )}
                              {deal.trade_in_value != null &&
                                deal.trade_in_value > 0 && (
                                  <span className="micro-badge micro-badge-amber">
                                    Trade-in
                                  </span>
                                )}
                              {stale && (
                                <span className="micro-badge micro-badge-red">
                                  <AlertTriangle className="h-2.5 w-2.5" />
                                  Stale
                                </span>
                              )}
                              {deal.forecast_category && (
                                <span className="micro-badge micro-badge-violet">
                                  {deal.forecast_category}
                                </span>
                              )}
                            </div>
                          )}

                          {deal.next_follow_up && (
                            <p className="mt-1.5 text-[10px] text-[var(--text-tertiary)] flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(
                                deal.next_follow_up,
                              ).toLocaleDateString()}
                            </p>
                          )}

                          {/* Stage move buttons */}
                          <div className="mt-2 pt-2 border-t border-[var(--border-subtle)] flex gap-1 flex-wrap">
                            {STAGES.filter((s) => s.key !== deal.stage)
                              .slice(0, 3)
                              .map((s) => (
                                <button
                                  key={s.key}
                                  onClick={() =>
                                    updateMutation.mutate({
                                      id: deal.id,
                                      stage: s.key,
                                    })
                                  }
                                  className="flex items-center gap-0.5 rounded-[var(--radius-pill)] bg-[var(--surface-sunken)] px-2 py-0.5 text-[9px] font-medium text-[var(--text-tertiary)] hover:bg-[var(--color-interactive-primary)]/10 hover:text-[var(--color-interactive-primary)] transition-colors"
                                >
                                  <ChevronRight className="h-2.5 w-2.5" />
                                  {s.label}
                                </button>
                              ))}
                          </div>
                        </div>
                      );
                    })}
                    {stageDeals.length === 0 && (
                      <div className="flex items-center justify-center py-6 text-xs text-[var(--text-tertiary)]">
                        No deals
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ══════════════════════════════════════════════
          §3  Revenue Forecast
          ══════════════════════════════════════════════ */}
      {forecast && forecast.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-[var(--text-tertiary)]" />
            <h3 className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
              Revenue Forecast
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {forecast.map((p) => (
              <Card key={p.period} padding="sm">
                <p className="text-sm font-semibold text-[var(--text-primary)] mb-3">
                  {p.period}
                </p>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-[var(--text-tertiary)]">Commit</span>
                    <span className="font-semibold tabular-nums text-[var(--text-primary)]">
                      {fmtCompact(p.commit)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-[var(--text-tertiary)]">
                      Best Case
                    </span>
                    <span className="font-semibold tabular-nums text-[var(--text-primary)]">
                      {fmtCompact(p.best_case)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-[var(--text-tertiary)]">
                      Pipeline
                    </span>
                    <span className="font-semibold tabular-nums text-[var(--text-primary)]">
                      {fmtCompact(p.pipeline_value)}
                    </span>
                  </div>
                  <div className="divider my-1" />
                  <div className="flex justify-between text-xs">
                    <span className="text-violet-600 font-medium">
                      Weighted
                    </span>
                    <span className="font-bold tabular-nums text-violet-600">
                      {fmtCompact(p.weighted_total)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-[var(--text-success)] font-medium">
                      Closed Won
                    </span>
                    <span className="font-bold tabular-nums text-[var(--text-success)]">
                      {fmt(p.closed_won)}
                    </span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default DealsPage;
